import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import { api } from "../lib/api";
import { Trash2, Plus, Save, Shield } from "lucide-react";
import { Eyebrow } from "../components/Bits";

const KEYS = ["budget", "preferred_areas", "family_size", "property_type", "moving_timeline", "cuisines"];

export default function Memory() {
  const { user, ready } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  const [prefs, setPrefs] = useState({});
  const [newKey, setNewKey] = useState("");
  const [newVal, setNewVal] = useState("");

  useEffect(() => { if (ready && !user) nav("/auth"); }, [ready, user, nav]);
  useEffect(() => { if (user) api.get("/me/preferences").then((r) => setPrefs(r.data.preferences || {})); }, [user]);

  const save = async () => { const { data } = await api.put("/me/preferences", { preferences: prefs }); setPrefs(data.preferences); toast.success(t("memory.save")); };
  const removeKey = (k) => { const c = { ...prefs }; delete c[k]; setPrefs(c); };
  const addKey = () => { if (!newKey) return; setPrefs({ ...prefs, [newKey]: newVal }); setNewKey(""); setNewVal(""); };
  const deleteAll = async () => { await api.delete("/me/preferences"); setPrefs({}); toast.success(t("memory.delete_all")); };

  if (!ready || !user) return <div className="p-16 text-white/50">{t("common.loading")}</div>;

  return (
    <div className="max-w-[900px] mx-auto px-5 lg:px-8 py-14" data-testid="memory-page">
      <Eyebrow>{t("memory.eyebrow")}</Eyebrow>
      <h1 className="font-display text-4xl text-white mt-2">{t("memory.title")}</h1>
      <p className="text-white/55 mt-3 flex items-center gap-2"><Shield size={15} className="text-gold" /> {t("memory.sub")}</p>

      <div className="glass rounded-2xl p-7 mt-8 space-y-4">
        {KEYS.map((k) => (
          <div key={k} className="flex items-center gap-3">
            <label className="w-40 text-sm text-white/60 shrink-0">{t(`memory.fields.${k}`)}</label>
            <input value={prefs[k] || ""} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.value })} data-testid={`pref-${k}`} placeholder={t("memory.not_set")}
              className="flex-1 bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold/40" />
          </div>
        ))}
        {Object.keys(prefs).filter((k) => !KEYS.includes(k)).map((k) => (
          <div key={k} className="flex items-center gap-3">
            <label className="w-40 text-sm text-white/60 shrink-0 truncate">{k}</label>
            <input value={prefs[k]} onChange={(e) => setPrefs({ ...prefs, [k]: e.target.value })} className="flex-1 bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-gold/40" />
            <button onClick={() => removeKey(k)} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
          </div>
        ))}
        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <input value={newKey} onChange={(e) => setNewKey(e.target.value)} placeholder={t("memory.add")} className="w-40 bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold/40" />
          <input value={newVal} onChange={(e) => setNewVal(e.target.value)} placeholder={t("memory.value")} className="flex-1 bg-input/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-gold/40" />
          <button onClick={addKey} data-testid="add-pref" className="p-2 rounded-lg bg-white/5 text-gold hover:bg-white/10"><Plus size={16} /></button>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-5">
        <button onClick={save} data-testid="save-prefs" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gold text-ink font-semibold hover:brightness-110 transition-all duration-200"><Save size={15} /> {t("memory.save")}</button>
        <button onClick={deleteAll} data-testid="delete-prefs" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors duration-200"><Trash2 size={15} /> {t("memory.delete_all")}</button>
      </div>
    </div>
  );
}
